import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Nodes that make a tree worth having. Five connected nodes is the activation definition — the
 * smallest tree that has demonstrably delivered something — and it is what the backend's
 * `fifth_node_added` event and every Scale Gate are measured against. Keep the two in step.
 */
export const ACTIVATION_NODE_COUNT = 5;

const STORAGE_PREFIX = "tt_milestone_five_nodes:";

/**
 * localStorage throws rather than no-ops in some privacy modes and embedded webviews, so both
 * accessors fail closed: unreadable storage means "not celebrated yet", which at worst shows the
 * moment twice. Losing it entirely would be the worse outcome.
 */
const wasCelebrated = (treeId: string): boolean => {
	try {
		return window.localStorage.getItem(STORAGE_PREFIX + treeId) !== null;
	} catch {
		return false;
	}
};

const markCelebrated = (treeId: string): void => {
	try {
		window.localStorage.setItem(STORAGE_PREFIX + treeId, new Date().toISOString());
	} catch {
		// Storage unavailable. The milestone may show again on another visit; nothing breaks.
	}
};

/**
 * Watches a tree's node count and reports the moment it crosses the activation threshold.
 *
 * Deliberately a *transition*, not a threshold: `count >= 5` would fire the instant someone opened
 * a tree they finished last month, congratulating them for work they have long since forgotten
 * doing. So the previous count starts undefined and the first render only records it — the
 * milestone can fire on the second observation at the earliest, which is exactly when a node was
 * added in front of the user.
 *
 * It is also remembered per tree, so deleting a node and adding it back does not stage the
 * celebration again.
 *
 * @param treeId Tree being edited, or undefined while it loads.
 * @param nodeCount Current number of nodes.
 * @param enabled False for a read-only viewer — they did not build this tree.
 */
export const useActivationMilestone = (
	treeId: string | undefined,
	nodeCount: number,
	enabled: boolean
): { isCelebrating: boolean; dismiss: () => void } => {
	const [isCelebrating, setIsCelebrating] = useState(false);
	const previousCount = useRef<number | null>(null);
	// Which tree `previousCount` describes, so switching trees does not read across as a jump.
	const previousTreeId = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!treeId || !enabled) {
			return;
		}

		if (previousTreeId.current !== treeId) {
			previousTreeId.current = treeId;
			previousCount.current = nodeCount;

			return;
		}

		const previous = previousCount.current;
		previousCount.current = nodeCount;

		if (previous === null || previous >= ACTIVATION_NODE_COUNT || nodeCount < ACTIVATION_NODE_COUNT) {
			return;
		}

		if (!wasCelebrated(treeId)) {
			markCelebrated(treeId);
			setIsCelebrating(true);
		}
	}, [enabled, nodeCount, treeId]);

	const dismiss = useCallback((): void => setIsCelebrating(false), []);

	return { isCelebrating, dismiss };
};
